/* eslint-disable @typescript-eslint/camelcase */
import axios, {
  Method,
  AxiosError,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
}                       from 'axios';
import { store }        from '@store/index';
import { Notification } from '@components/Notification/Notification';
import { logout }       from '@modules/auth/queries/authQueries';
import { getToken }     from '@modules/auth/authReducer';
import { ICuidPayload } from '@typings/accountManagement';

type TAttachmentType = 'pdf' | 'attachment';

interface IRequestOptions {
  isFormData? : boolean;
  isIdentity? : boolean;
}

const { API_URL }             = process.env;
const { IDENTITY_SERVER_URL } = process.env;
const { UI_BUILD_NUMBER }     = process.env;

enum STATUS_CODE {
  UNAUTHORIZED = 401,
  BLOCKED      = 403,
}

const PARAMS_METHODS = ['get', 'GET', 'delete', 'DELETE'];

const ACCEPT_TYPE: Record<TAttachmentType, string> = {
  pdf        : 'application/pdf',
  attachment : 'application/octet-stream',
};

axios.interceptors.request.use(
  (config): AxiosRequestConfig => {
    const state = store.createdStore.getState();
    const token = getToken(state);

    if (token) {
      return ({
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        },
      });
    }

    return config;
  },
);

axios.interceptors.response.use(
  (response): AxiosResponse | Promise<AxiosResponse> => {
    const { ui_build_number_intranet } = response.headers;
    if (
      ui_build_number_intranet
      && UI_BUILD_NUMBER
      && ui_build_number_intranet !== UI_BUILD_NUMBER
    ) {
      // eslint-disable-next-line no-restricted-globals
      location.reload();
    }

    return response;
  },
  async (error): Promise<AxiosError> => {
    const { status } = error.response ?? {};

    if ([
      STATUS_CODE.BLOCKED,
      STATUS_CODE.UNAUTHORIZED,
    ].includes(status)) {
      await logout();
    }

    return Promise.reject(error);
  },
);

export function callApi<T = void>(
  endpoint : string,
  method   : Method = 'get',
  data?    : FormData | Record<string, unknown> | null | object | any,
  options? : IRequestOptions | null,
  params   : Record<string, unknown> | ICuidPayload | null = {},
): AxiosPromise<T> {
  const headers = {
    'Content-Type': options?.isFormData ? 'multipart/form-data' : 'application/json',
  };

  const requestData: AxiosRequestConfig = {
    headers,
    method,
  };

  if (PARAMS_METHODS.includes(method)) {
    requestData.params = data;
  } else {
    requestData.data   = data;
    requestData.params = params;
  }

  const baseURL: string = options?.isIdentity ? `${IDENTITY_SERVER_URL}` : `${API_URL}`;

  return axios({
    url: `${baseURL}/${endpoint}`,
    ...requestData,
  });
}

export const requestAttachments = async (
  endpoint       : string,
  attachmentType : TAttachmentType,
  params?        : Record<string, unknown> | ICuidPayload | null,
): Promise<void> => {
  Notification.enqueueSnackbar(
    'Downloading...',
    'info',
  );

  const requestConfig: AxiosRequestConfig = {
    url          : `${API_URL}/${endpoint}`,
    method       : 'get',
    responseType : 'arraybuffer',
    headers      : {
      'Content-Type' : 'application/json',
      Accept         : ACCEPT_TYPE[attachmentType],
    },
    params,
  };

  const { data, headers } = await axios(requestConfig);

  const blob = new Blob([data], { type: ACCEPT_TYPE[attachmentType] });

  const url      = window.URL.createObjectURL(blob);
  const link     = document.createElement('a');
  const fileName = headers['content-disposition']
    .split('filename=')[1]
    .split(';')[0]
    .replace(/"/g, '');

  link.href     = url;
  link.download = fileName;
  link.click();

  window.URL.revokeObjectURL(url);

  Notification.closeSnackbar();
};

/* eslint-enable @typescript-eslint/camelcase */
